import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/objectSpread2";
import _get from "lodash/get";
import React, { useEffect } from "react";
import compare from "mibaby-common/util/compare";
import { mBFetch as fetch } from "mibaby-common/util/fetch";
import useComments from "./useComments";
import { POLYMORPH, matchOne, matchLoaded, fromQuery, toQuery, entityArgs, matchModel } from "./common";
import { jsx as _jsx } from "react/jsx-runtime";
export var Context = /*#__PURE__*/React.createContext();
export var UNLOADED = 0;
export var SCHEDULED = 1;
export var LOADING = 2;
export var LOADED = 3;

/**
 * Reducer for basic comment state changes
 * @param {object} state
 * @param {object} action
 */
export var reducer = function reducer(state, action) {
  var type = action.type,
    comment = action.comment,
    _action$data = action.data,
    data = _action$data === void 0 ? [] : _action$data;
  var match = matchOne("id", "cid");
  var set = function set(list, comment) {
    var fn = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : function (a) {
      return a;
    };
    var i = list.findIndex(match(comment));
    list[i > -1 ? i : list.length] = fn(comment);
    return list.filter(function (a) {
      return a;
    }).sort(function (a, b) {
      return compare(a.created, b.created);
    });
  };
  switch (type) {
    case "reset":
      state = [];
    case "set":
      return data.reduce(function (draft, _ref) {
        var comment = _ref.Comment;
        return set(draft, comment);
      }, state);
    case "add":
    case "edit":
      return set(state, comment);
    case "delete":
      return set(state, comment, function () {
        return null;
      });
    case "like":
      var likes = comment.AGG_LIKE || {
        user: 0,
        sum: 0
      };
      return set(state, comment, function () {
        return _objectSpread(_objectSpread({}, comment), {}, {
          AGG_LIKE: {
            user: likes.user ? 0 : 1,
            sum: likes.sum + (likes.user ? -1 : 1)
          }
        });
      });
    default:
      return state;
  }
};

// ---------------------------------------------
//              API connection
// ---------------------------------------------

export var createApi = function createApi() {
  var endpoint = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : "/api_comments";
  return function (action) {
    var method = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "GET";
    var data = arguments.length > 2 ? arguments[2] : undefined;
    return fetch("".concat(endpoint, "/").concat(action || ""), {
      method: method,
      body: data && Object.keys(data).reduce(function (form, key) {
        return form.append(key, JSON.stringify(data[key])) || form;
      }, new FormData())
    });
  };
};

/**
 * Make calls to the comments API
 * @param {string|int} action
 * @param {string} method
 * @param {*} data
 */
export var api = createApi();

/**
 * Get the current load state for an args object
 */
export var getLoaded = function getLoaded(context, args) {
  var match = matchLoaded(args);
  return Math.max.apply(Math, [UNLOADED].concat(_toConsumableArray(Object.entries(context.loaded).filter(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 1),
      k = _ref3[0];
    return match(fromQuery(k));
  }).map(function (_ref4) {
    var _ref5 = _slicedToArray(_ref4, 2),
      v = _ref5[1];
    return v;
  }))));
};

/**
 * Check if there are any active loading operations for the given args object
 */
export var isLoading = function isLoading(context, args) {
  var match = matchModel(args);
  return Object.entries(context.loaded).some(function (_ref6) {
    var _ref7 = _slicedToArray(_ref6, 2),
      k = _ref7[0],
      v = _ref7[1];
    return [SCHEDULED, LOADING].includes(v) && match(fromQuery(k));
  });
};

/**
 * Check if the given args object is loaded, either by its loaded state or
 * by comparing the # of loaded comments to the loaded total count
 */
export var isLoaded = function isLoaded(context, args) {
  if (getLoaded(context, args) === LOADED) {
    return true;
  } else if (POLYMORPH.find(function (k) {
    return args[k];
  })) {
    args = entityArgs(args);
    var count = context.count[toQuery(args)];
    return count !== undefined && count <= getComments(context, args).length;
  } else {
    return false;
  }
};

/**
 * Load missing stuff
 * @param {object} context
 * @param {object} args
 * @param {boolean} load Load missing comments as well
 */
export var useCheckLoad = function useCheckLoad(context, args) {
  var load = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  return useEffect(function () {
    if (!getLoaded(context, {
      config: 1
    })) {
      context.dispatch({
        type: "load",
        config: 1
      });
    }
    if (POLYMORPH.every(function (k) {
      return args[k];
    })) {
      if (!getLoaded(context, _objectSpread(_objectSpread({}, entityArgs(args)), {}, {
        count: 1
      }))) {
        context.dispatch(_objectSpread(_objectSpread({
          type: "load"
        }, entityArgs(args)), {}, {
          count: 1
        }));
      }
      if (load && !getLoaded(context, args)) {
        context.dispatch(_objectSpread({
          type: "load"
        }, args));
      }
    }
  }, [context, args, load]);
};

/**
 * Filter and sort the comments list by an args object
 * @param {object} context
 * @param {object} args
 */
export var getComments = function getComments(context, args) {
  var comments = context.comments.filter(matchModel(args));
  if (args.top) {
    comments = comments.sort(function (a, b) {
      return compare(_get(b, "AGG_LIKE.sum", 0), _get(a, "AGG_LIKE.sum", 0)) || compare(a.created, b.created);
    }).slice(0, parseInt(args.top) || 3);
  }
  return comments;
};

/**
 * Holds and provides comments.
 * @param {{contextType: string, contextId: number, autoload: boolean, children: array}}
 */
export var CommentProvider = function CommentProvider(_ref8) {
  var user = _ref8.user,
    api = _ref8.api,
    children = _ref8.children;
  var context = useComments(user, api);
  return /*#__PURE__*/_jsx(Context.Provider, {
    value: context,
    children: children
  });
};
export default CommentProvider;
//# sourceMappingURL=Context.js.map